import React from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { router } from "@/components/App/router";
import {
  backButton,
  emitEvent,
  init,
  locationManager,
  mockTelegramEnv,
  swipeBehavior,
  viewport,
} from "@telegram-apps/sdk-react";
import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";

const noInsets = {
  left: 0,
  top: 0,
  bottom: 0,
  right: 0,
} as const;

const themeParams = {
  accent_text_color: "#6ab2f2",
  bg_color: "#17212b",
  button_color: "#5288c1",
  button_text_color: "#ffffff",
  destructive_text_color: "#ec3942",
  header_bg_color: "#17212b",
  hint_color: "#708499",
  link_color: "#6ab3f3",
  secondary_bg_color: "#232e3c",
  section_bg_color: "#17212b",
  section_header_text_color: "#6ab3f3",
  subtitle_text_color: "#708499",
  text_color: "#f5f5f5",
} as const;

function isTelegramWebApp(): boolean {
  return Boolean(
    (window as any).Telegram?.WebApp &&
      (window as any).Telegram.WebApp.initData?.length
  );
}

if (!isTelegramWebApp()) {
  mockTelegramEnv({
    launchParams: {
      tgWebAppThemeParams: themeParams,
      tgWebAppData: new URLSearchParams([
        [
          "user",
          JSON.stringify({
            id: 0,
            first_name: "Pavel",
          }),
        ],
        ["hash", ""],
        ["signature", ""],
        ["auth_date", Date.now().toString()],
      ]),
      tgWebAppStartParam: "debug",
      tgWebAppVersion: "8",
      tgWebAppPlatform: "tdesktop",
    },
    onEvent(e) {
      if (e[0] === "web_app_request_theme") {
        return emitEvent("theme_changed", { theme_params: themeParams });
      }
      if (e[0] === "web_app_request_viewport") {
        return emitEvent("viewport_changed", {
          height: window.innerHeight,
          width: window.innerWidth,
          is_expanded: true,
          is_state_stable: true,
        });
      }
      if (e[0] === "web_app_request_content_safe_area") {
        return emitEvent("content_safe_area_changed", noInsets);
      }
      if (e[0] === "web_app_request_safe_area") {
        return emitEvent("safe_area_changed", noInsets);
      }
    },
  });
}

init({
  acceptCustomStyles: true,
});

viewport.mount();
locationManager.mount();
swipeBehavior.mount();
backButton.mount();

const canRegisterServiceWorker =
  "serviceWorker" in navigator &&
  (window.location.protocol === "https:" ||
    window.location.hostname === "localhost");

if (canRegisterServiceWorker) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");
const root = createRoot(rootElement);

root.render(
  <ErrorBoundary>
    <I18nextProvider i18n={i18n}>
      <RouterProvider router={router} />
    </I18nextProvider>
  </ErrorBoundary>
);
